import { SelectProps } from 'antd';
import { get } from 'lodash-es';
import { useCallback } from 'react';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';

import FieldWithLabel from '../../../common/components/Fields/FieldWithLabel';
import { FieldProps } from '../../../common/components/Fields/types';
import AsyncSelect from '../../../common/components/Input/AsyncSelect';
import { useProductGroupOptionsLazyQuery } from '../graphql/productGroups.generated';

export type SelectFieldProps<TFieldValues extends FieldValues> = SelectProps &
  FieldProps & {
    control: Control<TFieldValues>;
    name: Path<TFieldValues>;
    initialOption?: { value: number; label: string };
  };

function ProductGroupSelectField<TFieldValues extends FieldValues>({
  name,
  label,
  required,
  control,
  hideError,
  onChange,
  ...props
}: SelectFieldProps<TFieldValues>) {
  const [fetchProductGroup] = useProductGroupOptionsLazyQuery({
    fetchPolicy: 'no-cache',
  });
  const fetchItems = useCallback(
    async (productGroupName: string) => {
      const { data } = await fetchProductGroup({
        variables: {
          filter: {
            productGroupName: productGroupName.trim().length
              ? productGroupName
              : undefined,
          },
          pagination: { skip: 0, take: 20 },
        },
      });
      return (
        data?.adminProductGroups?.edges.map(({ node }) => ({
          label: node.name,
          value: node.id,
        })) || []
      );
    },
    [fetchProductGroup]
  );

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, formState }) => {
        const error = get(formState.errors, name);
        const errorMessage =
          !hideError && typeof error?.message === 'string'
            ? error.message
            : undefined;
        const status = !hideError && errorMessage ? 'error' : '';

        return (
          <FieldWithLabel label={label} required={required}>
            <AsyncSelect
              initialLoad
              className={'w-full'}
              fetchItems={fetchItems}
              status={status}
              {...field}
              onChange={(v, option) => {
                field.onChange(v);

                onChange?.(v, option);
              }}
              {...props}
            />

            {!hideError && errorMessage && (
              <p className={'text-xs text-red-500'}>{errorMessage}</p>
            )}
          </FieldWithLabel>
        );
      }}
    />
  );
}

export default ProductGroupSelectField;
